import React, { useState, useEffect } from 'react';

// Add this mapping function for household contents
const mapAddressDataForHousehold = (addressData: any) => {
  console.log('🔄 Raw address data for household mapping:', addressData);
  console.log('🔑 Available keys in address data:', Object.keys(addressData));

// Add this mapping function for API submission
const mapHouseholdContentsForAPI = (contentsData: HouseholdContents): any => {
  console.log('🔄 Mapping household contents for API:', contentsData);
  
  return {
    ...contentsData,
    // Ensure arrays are properly formatted for the backend
    perimeterType: Array.isArray(contentsData.perimeterType) ? 
      contentsData.perimeterType : 
      (contentsData.perimeterType ? [contentsData.perimeterType] : []),
    wallMaterials: Array.isArray(contentsData.wallMaterials) ? 
      contentsData.wallMaterials : 
      (contentsData.wallMaterials ? [contentsData.wallMaterials] : []),
    roofMaterials: Array.isArray(contentsData.roofMaterials) ? 
      contentsData.roofMaterials : 
      (contentsData.roofMaterials ? [contentsData.roofMaterials] : []),
    floorMaterials: Array.isArray(contentsData.floorMaterials) ? 
      contentsData.floorMaterials : 
      (contentsData.floorMaterials ? [contentsData.floorMaterials] : []),
    otherSurroundingArea: Array.isArray(contentsData.otherSurroundingArea) ? 
      contentsData.otherSurroundingArea : 
      (contentsData.otherSurroundingArea ? [contentsData.otherSurroundingArea] : []),
    // Convert insuredValue to string for backend
    insuredValue: contentsData.insuredValue?.toString() || '0.00',
    // Ensure boolean fields are properly sent
    conductsBusiness: Boolean(contentsData.conductsBusiness),
    hasSurveillanceCamera: Boolean(contentsData.hasSurveillanceCamera),
    hasExternalSlidingDoor: Boolean(contentsData.hasExternalSlidingDoor),
    // ... include all other boolean fields similarly
  };
};
  
  // Use the exact field names from your backend
  const mapped = {
    addressLine1: addressData.street || '', // This is 'street' from backend
    addressLine2: addressData.address_line_2 || '', // Optional field
    suburb: addressData.state || '', // Suburb is 'state' from backend (region in DB)
    cityTown: addressData.city || '', // Note: household uses cityTown instead of city
    postalCode: addressData.zipCode || '', // This is 'zipCode' from backend
    region: addressData.province || '', // Region is 'province' from backend
    country: addressData.country || 'South Africa' // Default to South Africa
  };

  console.log('✅ Mapped address data for household:', mapped);
  return mapped;
};

interface EditableHouseholdContents extends HouseholdContents {
  isEditing?: boolean;
}

interface EditableHouseholdContentsCardProps {
  contents: EditableHouseholdContents;
  index: number;
  onSave: (index: number, updatedData: EditableHouseholdContents) => void;
  onCancel: (index: number) => void;
  onEdit: (index: number) => void;
  cardNumber?: number;
  cardTitle?: string;
  showCardNumber?: boolean;
}

interface HouseholdContents {
  id?: string | number;
  quoteId?: number | string;
  yearsLivedOnProperty?: number;
  burglarBarType?: string;
  dayTimeOccupantType?: string;
  coverType?: string;
  insuredValue?: number;
  conductsBusiness?: boolean;
  hasSurveillanceCamera?: boolean;
  hasExternalSlidingDoor?: boolean;
  hasSecurityGate?: boolean;
  inSecurityComplex?: boolean;
  hasAlarmRecords?: boolean;
  hasNeighborhoodWatch?: boolean;
  hasAccidentalDamageCover?: boolean;
  accidentalDamageValue?: number;
  accidental_damage_value?: number;
  hasPowerSurgeCover?: boolean;
  powerSurgeValue?: number;
  power_surge_value?: number;
  alarmSystemType?: string;
  perimeterType?: string[];
  addressLine1?: string;
  addressLine2?: string;
  suburb?: string;
  cityTown?: string;
  postalCode?: string;
  region?: string;
  country?: string;
  propertyType?: string;
  residenceType?: string;
  buildingAge?: string;
  situatedType?: string;
  hasSwimmingPool?: boolean;
  hasMotorizedGate?: boolean;
  isPropertyUnoccupied?: boolean;
  isCommune?: boolean;
  underConstruction?: boolean;
  builtOnSteepSlope?: boolean;
  hasRetainingWalls?: boolean;
  nearWaterBody?: boolean;
  hasRisingDamp?: boolean;
  hasWallCracks?: boolean;
  historyOfLandslip?: boolean;
  hasOutbuilding?: boolean;
  hasPerimeterFence?: boolean;
  hasElectricFence?: boolean;
  hasThatchRoof?: boolean;
  wallMaterials?: string[];
  roofMaterials?: string[];
  floorMaterials?: string[];
  otherSurroundingArea?: string[];
  premium?: number;
  excess?: number;
  noClaimBonus?: string;
  isTemp?: boolean;
  
}

interface HouseholdContentsErrors {
  yearsLivedOnProperty: string;
  burglarBarType: string;
  dayTimeOccupantType: string;
  coverType: string;
  insuredValue: string;
  addressLine1: string;
  suburb: string;
  cityTown: string;
  postalCode: string;
  region: string;
  country: string;
  server: string;
}

interface AddHouseholdContentsModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSave: (contentsData: HouseholdContents) => void;
  clientId: number;
  clientData?: any;
  quoteId: number | string;
  contents?: HouseholdContents;
  mode?: 'create' | 'edit' | 'view';
  setSaveStatus?: (status: 'idle' | 'loading' | 'success' | 'error') => void;
  setSaveMessage?: (message: string) => void;
  onEdit?: () => void;
}

// Format currency values for display
  const formatCurrencyForDisplay = (value: number | string): string => {
    if (!value) return '';
    
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    
    if (isNaN(numValue)) return value.toString();
    
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

const API_URL = process.env.REACT_APP_API_URL || '';

const perimeterTypeOptions = ['Walls', 'Palisades', 'Electric Fencing', 'Barbed Wire'];
const wallMaterialsOptions = ['Brick', 'Cement', 'Corrugated Iron', 'Glass', 'Prefab', 'Asbestos', 'Wood', 'Other'];
const roofMaterialsOptions = ['Concrete', 'Corrugated Iron', 'Glass', 'Thatch', 'Asbestos', 'Wood', 'Tiles'];
const floorMaterialsOptions = ['Carpet', 'Sealed', 'Tiles', 'Wooden Floors'];
const surroundingAreaOptions = [
  'Farm', 'Plot', 'Golf Estate', 'Country Estate / Residential Area', 'Security Complex',
  'Complex With No Access Control', 'Boomed Off Suburb', 'Suburb', 'Industrial Area',
  'Commercial Area', 'Inner City / CBD', 'Informal Settlement', 'Retirement Village',
  'Nature Reserve', 'Holiday Resort', 'School/University', 'Parks', 'Highways',
  'Railway Lines', 'Shops/Cafes', 'Squatter Camps', 'Open Fields', 'Other'
];

// Add this helper function for converting to array
const convertToArray = (value: string | string[] | undefined): string[] => {
  if (Array.isArray(value)) {
    return value;
  }
  return value ? [value] : [];
};

const AddHouseholdContentsModal: React.FC<AddHouseholdContentsModalProps> = ({ 
  isVisible,
  onClose,
  onSave,
  clientId,
  clientData,
  quoteId ,
  contents,
  mode = 'create',
  setSaveStatus,
  setSaveMessage,
  onEdit
}) => {
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  const clearAllSelections = (field: 'perimeterType' | 'wallMaterials' | 'roofMaterials' | 'floorMaterials' | 'otherSurroundingArea') => {
    setContentsFormData(prev => ({
      ...prev,
      [field]: []
    }));
  };

  const saveHouseholdContentsToDB = async (contentsData: any, quoteIdParam?: number): Promise<any> => {
  try {
    // Use the parameter if provided, otherwise use the prop
    const currentQuoteId = quoteIdParam || quoteId;
    
    console.log('🚀 STARTING saveHouseholdContentsToDB function:', {
      hasQuoteId: !!currentQuoteId,
      quoteId: currentQuoteId,
      clientId,
      addressLine1: contentsData.address_line1 || contentsData.addressLine1
    });
    
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    if (!currentQuoteId) {
      throw new Error('quoteId is required to save to database');
    }

    // Prepare payload
    const payload = {
      householdContents: [contentsData],
      accidentalDamageValue: contentsData.accidentalDamageValue || 0,
      powerSurgeValue: contentsData.powerSurgeValue || 0,
      clientId: clientId,
      quoteId: currentQuoteId.toString()
    };

    console.log('📤 Making POST request to:', `${API_URL}/api/quotes/${currentQuoteId}/save-household`);
    console.log('📦 Payload:', JSON.stringify(payload, null, 2));

    const response = await fetch(`${API_URL}/api/quotes/${currentQuoteId}/save-household`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API error response:', errorText);
      throw new Error(`Server error: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log('✅ Database save successful:', result);
    
    return result;
    
  } catch (error) {
    console.error('❌ Error in saveHouseholdContentsToDB:', error);
    throw error;
  }
};
  
  const handleCheckboxGroupChange = (
    field: 'perimeterType' | 'wallMaterials' | 'roofMaterials' | 'floorMaterials' | 'otherSurroundingArea',
    option: string,
    isChecked: boolean
  ) => {
    setContentsFormData(prev => {
      const currentArray = (prev[field] as string[]) || [];
      
      if (isChecked) {
        // Add option if checked
        return {
          ...prev,
          [field]: [...currentArray, option]
        };
      } else {
        // Remove option if unchecked
        return {
          ...prev,
          [field]: currentArray.filter(item => item !== option)
        };
      }
    });
  };

  // Initialize form data based on mode
  const getInitialFormData = (): Partial<HouseholdContents> => {
    if (isEditMode || isViewMode) {

      // For edit/view mode, use the passed contents data
      return {
        yearsLivedOnProperty: contents?.yearsLivedOnProperty || undefined,
        burglarBarType: contents?.burglarBarType || '',
        dayTimeOccupantType: contents?.dayTimeOccupantType || '',
        coverType: contents?.coverType || '',
        insuredValue: contents?.insuredValue || undefined,
        conductsBusiness: contents?.conductsBusiness || false,
        hasSurveillanceCamera: contents?.hasSurveillanceCamera || false,
        hasExternalSlidingDoor: contents?.hasExternalSlidingDoor || false,
        hasSecurityGate: contents?.hasSecurityGate || false,
        inSecurityComplex: contents?.inSecurityComplex || false,
        hasAlarmRecords: contents?.hasAlarmRecords || false,
        hasNeighborhoodWatch: contents?.hasNeighborhoodWatch || false,
        hasAccidentalDamageCover: contents?.hasAccidentalDamageCover || false,
        accidentalDamageValue: contents?.accidentalDamageValue || undefined,
        hasPowerSurgeCover: contents?.hasPowerSurgeCover || false,
        powerSurgeValue: contents?.powerSurgeValue || undefined,
        alarmSystemType: contents?.alarmSystemType || '',
        perimeterType: convertToArray(contents?.perimeterType),
        addressLine1: contents?.addressLine1 || '',
        addressLine2: contents?.addressLine2 || '',
        suburb: contents?.suburb || '',
        cityTown: contents?.cityTown || '',
        postalCode: contents?.postalCode || '',
        region: contents?.region || '',
        country: contents?.country || '',
        propertyType: contents?.propertyType || '',
        residenceType: contents?.residenceType || '',
        buildingAge: contents?.buildingAge || '',
        situatedType: contents?.situatedType || '',
        hasSwimmingPool: contents?.hasSwimmingPool || false,
        hasMotorizedGate: contents?.hasMotorizedGate || false,
        isPropertyUnoccupied: contents?.isPropertyUnoccupied || false,
        isCommune: contents?.isCommune || false,
        underConstruction: contents?.underConstruction || false,
        builtOnSteepSlope: contents?.builtOnSteepSlope || false,
        hasRetainingWalls: contents?.hasRetainingWalls || false,
        nearWaterBody: contents?.nearWaterBody || false,
        hasRisingDamp: contents?.hasRisingDamp || false,
        hasWallCracks: contents?.hasWallCracks || false,
        historyOfLandslip: contents?.historyOfLandslip || false,
        hasOutbuilding: contents?.hasOutbuilding || false,
        hasPerimeterFence: contents?.hasPerimeterFence || false,
        hasElectricFence: contents?.hasElectricFence || false,
        hasThatchRoof: contents?.hasThatchRoof || false,
        wallMaterials: convertToArray(contents?.wallMaterials),
        roofMaterials: convertToArray(contents?.roofMaterials),
        floorMaterials: convertToArray(contents?.floorMaterials),
        otherSurroundingArea: convertToArray(contents?.otherSurroundingArea),
        premium: contents?.premium || undefined,
        excess: contents?.excess || undefined,
        noClaimBonus: contents?.noClaimBonus || '',
      };
    }
    
    // For create mode, use empty defaults
    return {
      yearsLivedOnProperty: undefined,
      burglarBarType: '',
      dayTimeOccupantType: '',
      coverType: '',
      insuredValue: undefined,
      conductsBusiness: false,
      hasSurveillanceCamera: false,
      hasExternalSlidingDoor: false,
      hasSecurityGate: false,
      inSecurityComplex: false,
      hasAlarmRecords: false,
      hasNeighborhoodWatch: false,
      hasAccidentalDamageCover: false,
      hasPowerSurgeCover: false,
      alarmSystemType: '',
      perimeterType: [],
      addressLine1: '',
      addressLine2: '',
      suburb: '',
      cityTown: '',
      postalCode: '',
      region: '',
      country: '',
      propertyType: '',
      residenceType: '',
      buildingAge: '',
      situatedType: '',
      hasSwimmingPool: false,
      hasMotorizedGate: false,
      isPropertyUnoccupied: false,
      isCommune: false,
      underConstruction: false,
      builtOnSteepSlope: false,
      hasRetainingWalls: false,
      nearWaterBody: false,
      hasRisingDamp: false,
      hasWallCracks: false,
      historyOfLandslip: false,
      hasOutbuilding: false,
      hasPerimeterFence: false,
      hasElectricFence: false,
      hasThatchRoof: false,
      wallMaterials: [],
      roofMaterials: [],
      floorMaterials: [],
      otherSurroundingArea: [],
      premium: undefined,
      excess: undefined,
      noClaimBonus: '',
    };
  };

  const [contentsFormData, setContentsFormData] = useState<Partial<HouseholdContents>>(getInitialFormData);
  const [contentsErrors, setContentsErrors] = useState<HouseholdContentsErrors>({
    yearsLivedOnProperty: '',
    burglarBarType: '',
    dayTimeOccupantType: '',
    coverType: '',
    insuredValue: '',
    addressLine1: '',
    suburb: '',
    cityTown: '',
    postalCode: '',
    region: '',
    country: '',
    server: '',
    });

    const [accidentalDamageDisplay, setAccidentalDamageDisplay] = useState(
  contents?.accidentalDamageValue ?? contents?.accidental_damage_value 
    ? formatCurrencyForDisplay(contents.accidentalDamageValue ?? contents.accidental_damage_value ?? 0) 
    : ''
);

    const [powerSurgeDisplay, setPowerSurgeDisplay] = useState(
  contents?.powerSurgeValue ?? contents?.power_surge_value 
    ? formatCurrencyForDisplay(contents.powerSurgeValue ?? contents.power_surge_value ?? 0) 
    : ''
);

    const [isFetchingAddress, setIsFetchingAddress] = useState(false);

    const [isEditing, setIsEditing] = useState(mode === 'edit' || mode === 'create');

    // Update isEditing when mode changes
    useEffect(() => {
      setIsEditing(mode === 'edit' || mode === 'create');
    }, [mode]);

    // Handle edit button click
  const handleEditClick = () => {
    if (onEdit) {
      onEdit(); // Call parent's edit function
    } else {
      setIsEditing(true); // Fallback: edit locally
    }
  };

    // Use internal state for the modal's own state management
  const [saveStatusInternal, setSaveStatusInternal] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [saveMessageInternal, setSaveMessageInternal] = useState('');

    // FIXED: Safe wrapper functions that check if the props exist
  const handleSetSaveStatus = (status: 'idle' | 'loading' | 'success' | 'error') => {
    if (setSaveStatus) {
      setSaveStatus(status);
    }
    // Always update local state for internal use
    setSaveStatusInternal(status);
  };

  const handleSetSaveMessage = (message: string) => {
    if (setSaveMessage) {
      setSaveMessage(message);
    }
    // Always update local state for internal use
    setSaveMessageInternal(message);
  };

  // currency helper function
  const formatCurrencyInput = (value: string): string => {
    if (!value) return '';
    
    // Remove any existing formatting
    const numericValue = value.replace(/[^\d.]/g, '');
    
    // Split into whole and decimal parts
    const parts = numericValue.split('.');
    let wholePart = parts[0] || '';
    let decimalPart = parts[1] || '';
    
    // Limit decimal to 2 digits
    if (decimalPart.length > 2) {
      decimalPart = decimalPart.substring(0, 2);
    }
    
    // Format the whole number part with commas
    if (wholePart) {
      wholePart = parseInt(wholePart, 10).toLocaleString('en-US');
    }
    
    // Combine parts
    return decimalPart ? `${wholePart}.${decimalPart}` : wholePart;
  };

  const parseCurrencyInput = (value: string): number => {
    if (!value) return 0;
    // Remove commas and parse as float
    return parseFloat(value.replace(/,/g, ''));
  };

  // Currency display values
  const [currencyDisplay, setCurrencyDisplay] = useState({
    insuredValue: contents?.insuredValue ? formatCurrencyForDisplay(contents.insuredValue) : '',
    premium: contents?.premium ? formatCurrencyForDisplay(contents.premium) : '',
    excess: contents?.excess ? formatCurrencyForDisplay(contents.excess) : '',
  });

  React.useEffect(() => {
    setContentsFormData(getInitialFormData());
    // Reset currency display when form resets
    setCurrencyDisplay({
      insuredValue: contents?.insuredValue ? formatCurrencyForDisplay(contents.insuredValue) : '',
      premium: contents?.premium ? formatCurrencyForDisplay(contents.premium) : '',
      excess: contents?.excess ? formatCurrencyForDisplay(contents.excess) : '',
    });
    setAccidentalDamageDisplay(
    contents?.accidentalDamageValue ? formatCurrencyForDisplay(contents.accidentalDamageValue) : ''
    );
    setPowerSurgeDisplay(
      contents?.powerSurgeValue ? formatCurrencyForDisplay(contents.powerSurgeValue) : ''
    );
  }, [contents, mode, isVisible]);

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: 'insuredValue' | 'premium' | 'excess') => {
    const rawValue = e.target.value;
    
    // Allow only numbers, one decimal point, and commas
    let cleanValue = rawValue.replace(/[^\d.,]/g, '');
    
    // Ensure only one decimal point
    const decimalCount = (cleanValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      const firstDecimalIndex = cleanValue.indexOf('.');
      cleanValue = cleanValue.substring(0, firstDecimalIndex + 1) + 
                   cleanValue.substring(firstDecimalIndex + 1).replace(/\./g, '');
    }
    
    // Limit to 2 decimal places
    if (cleanValue.includes('.')) {
      const parts = cleanValue.split('.');
      if (parts[1].length > 2) {
        cleanValue = parts[0] + '.' + parts[1].substring(0, 2);
      }
    }
    
    // Update display value
    const formattedValue = formatCurrencyInput(cleanValue);
    setCurrencyDisplay(prev => ({
      ...prev,
      [fieldName]: formattedValue,
    }));
    
    // Update form data with parsed number
    const numericValue = parseCurrencyInput(cleanValue);
    setContentsFormData(prev => ({
      ...prev,
      [fieldName]: numericValue || undefined,
    }));
    
    setContentsErrors(prev => ({ ...prev, [fieldName]: '' }));
  };

  const handleAccidentalDamageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const rawValue = e.target.value;
  let cleanValue = rawValue.replace(/[^\d.,]/g, '');
  const decimalCount = (cleanValue.match(/\./g) || []).length;
  if (decimalCount > 1) {
    const firstDecimalIndex = cleanValue.indexOf('.');
    cleanValue = cleanValue.substring(0, firstDecimalIndex + 1) +
                 cleanValue.substring(firstDecimalIndex + 1).replace(/\./g, '');
  }
  if (cleanValue.includes('.')) {
    const parts = cleanValue.split('.');
    if (parts[1].length > 2) {
      cleanValue = parts[0] + '.' + parts[1].substring(0, 2);
    }
  }

  const formattedValue = formatCurrencyInput(cleanValue);
  setAccidentalDamageDisplay(formattedValue);

  const numericValue = parseCurrencyInput(cleanValue);
  setContentsFormData(prev => ({
    ...prev,
    accidentalDamageValue: numericValue || undefined,
  }));

  // Optional: clear error if you have one
  // setContentsErrors(prev => ({ ...prev, accidentalDamageValue: '' }));
};

const handlePowerSurgeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  // Exactly the same logic as above, just different field
  const rawValue = e.target.value;
  let cleanValue = rawValue.replace(/[^\d.,]/g, '');
  const decimalCount = (cleanValue.match(/\./g) || []).length;
  if (decimalCount > 1) {
    const firstDecimalIndex = cleanValue.indexOf('.');
    cleanValue = cleanValue.substring(0, firstDecimalIndex + 1) +
                 cleanValue.substring(firstDecimalIndex + 1).replace(/\./g, '');
  }
  if (cleanValue.includes('.')) {
    const parts = cleanValue.split('.');
    if (parts[1].length > 2) {
      cleanValue = parts[0] + '.' + parts[1].substring(0, 2);
    }
  }

  const formattedValue = formatCurrencyInput(cleanValue);
  setPowerSurgeDisplay(formattedValue);

  const numericValue = parseCurrencyInput(cleanValue);
  setContentsFormData(prev => ({
    ...prev,
    powerSurgeValue: numericValue || undefined,
  }));
};

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setContentsFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setContentsFormData(prev => ({
        ...prev,
        [name]: type === 'number' ? (value === '' ? undefined : Number(value)) : value,
      }));
      setContentsErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Updated function to fetch client address from the database
  const fetchClientAddress = async (clientId: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Use the specific client address endpoint
      const endpoint = `${API_URL}/api/addresses/client/${clientId}`;
      console.log(`🔍 Fetching household address from: ${endpoint}`);
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const errorText = await response.text();
        console.error('Non-JSON response:', errorText);
        throw new Error('Received non-JSON response from server');
      }

      const addressData = await response.json();
      console.log('✅ Household address data received:', addressData);

      if (!addressData) {
        throw new Error('No address data returned for this client');
      }

      return addressData;

    } catch (error) {
      console.error('Error fetching address data:', error);
      throw error;
    }
  };

  const handleFillFromClient = async () => {
  setIsFetchingAddress(true);
  try {
    console.log('🔄 Starting Fill from Client for Household Contents, clientId:', clientId);
    
    const addressData = await fetchClientAddress(clientId);

    // Use the mapping function to convert backend fields to household contents fields
    const mappedData = mapAddressDataForHousehold(addressData);

    console.log('🗺️ Final mapped address data for household form:', mappedData);

    // Update the form with the correctly mapped data
    setContentsFormData(prev => ({
      ...prev,
      ...mappedData,
    }));

    // Clear any previous errors
    setContentsErrors(prev => ({
      ...prev,
      addressLine1: '',
      suburb: '',
      cityTown: '',
      postalCode: '',
      region: '',
      country: '',
    }));

    // FIXED: Only show temporary success message, don't change the main save status
    handleSetSaveMessage('✓ Address filled successfully from client data!');

    // Clear success message after 3 seconds
    setTimeout(() => {
      handleSetSaveMessage('');
    }, 3000);

  } catch (error) {
    console.error('❌ Error in handleFillFromClient:', error);
    
    // FIXED: Only show temporary error message, don't change the main save status
    handleSetSaveMessage(
      error instanceof Error
        ? `❌ Failed to fetch address: ${error.message}`
        : '❌ Failed to fetch address: Unknown error'
    );

    // Clear error message after 5 seconds
    setTimeout(() => {
      handleSetSaveMessage('');
    }, 5000);
  } finally {
    setIsFetchingAddress(false);
  }
};

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🔍 DEBUG - START handleSubmit');
  console.log('📊 Form data state:', {
    hasAccidentalDamageCover: contentsFormData.hasAccidentalDamageCover,
    accidentalDamageValue: contentsFormData.accidentalDamageValue,
    hasPowerSurgeCover: contentsFormData.hasPowerSurgeCover,
    powerSurgeValue: contentsFormData.powerSurgeValue,
  });
  
  console.log('🔍 Modal props:', {
    quoteIdFromProps: quoteId,
    clientId: clientId,
    contentsHasQuoteId: contents?.quoteId,
    mode: mode
  });

    let currentQuoteId = quoteId;
  
    if (!currentQuoteId && mode === 'create') {
      console.log('✅ CREATE MODE: Allowing save without quoteId for local storage');
      // In create mode, we can save to local state without a quoteId
      // The quote will be created later and the household contents will be linked then
    } else if (!currentQuoteId && (mode === 'edit' || mode === 'view')) {
      // Only check for quoteId in edit/view modes
      console.error('❌ ERROR: No quoteId provided to modal for edit/view mode');
      handleSetSaveStatus('error');
      handleSetSaveMessage('Cannot save: No quote ID provided. Please close and try again.');
      return;
    }
    
    console.log('✅ Using quoteId:', currentQuoteId);
      
      // Simple prevention - only check if currently loading
      if (saveStatusInternal === 'loading') {
        console.log('🛑 Submission already in progress, skipping');
        return;
      }
    
    const newErrors: HouseholdContentsErrors = {
      yearsLivedOnProperty:
        !contentsFormData.yearsLivedOnProperty || isNaN(Number(contentsFormData.yearsLivedOnProperty)) || Number(contentsFormData.yearsLivedOnProperty) < 0
          ? 'Years lived on property must be a non-negative number'
          : '',
      burglarBarType: !contentsFormData.burglarBarType ? 'Burglar bar type is required' : '',
      dayTimeOccupantType: !contentsFormData.dayTimeOccupantType ? 'Day time occupant type is required' : '',
      coverType: !contentsFormData.coverType ? 'Cover type is required' : '',
      insuredValue:
        !contentsFormData.insuredValue || isNaN(Number(contentsFormData.insuredValue)) || Number(contentsFormData.insuredValue) <= 0
          ? 'Insured value must be a positive number'
          : '',
      addressLine1: !contentsFormData.addressLine1 ? 'Address Line 1 is required' : '',
      suburb: !contentsFormData.suburb ? 'Suburb is required' : '',
      cityTown: !contentsFormData.cityTown ? 'City/Town is required' : '',
      postalCode: !contentsFormData.postalCode ? 'Postal Code is required' : '',
      region: !contentsFormData.region ? 'Region is required' : '',
      country: !contentsFormData.country ? 'Country is required' : '',
      server: '',
    };
    
    setContentsErrors(newErrors);
    
    // Check if there are any errors
    if (Object.values(newErrors).some(error => error)) {
      console.log('Validation errors found:', newErrors);
      return;
    }

    // Set loading status
    handleSetSaveStatus('loading');

    try {
      console.log('🔄 Starting household contents submission...');

      // ──────────────────────────────────────────────────────────────
      // Step 1: Decide whether we should save to DB right now
      // ──────────────────────────────────────────────────────────────
      const hasRealQuoteId = !!quoteId && !isNaN(Number(quoteId)) && Number(quoteId) > 0;

      const shouldSaveToDatabaseNow =
        hasRealQuoteId &&                     // we have a real numeric quote ID
        mode !== 'create';                    // ← skip DB during "add product while creating quote"

      // ──────────────────────────────────────────────────────────────
      // Step 2: Prepare the contents object (isTemp depends on decision)
      // ──────────────────────────────────────────────────────────────
      const contentsForState: HouseholdContents = {
        ...contentsFormData,
        // Make sure arrays are properly handled
        perimeterType: Array.isArray(contentsFormData.perimeterType)
          ? contentsFormData.perimeterType
          : contentsFormData.perimeterType ? [contentsFormData.perimeterType] : [],
        wallMaterials: Array.isArray(contentsFormData.wallMaterials)
          ? contentsFormData.wallMaterials
          : contentsFormData.wallMaterials ? [contentsFormData.wallMaterials] : [],
        roofMaterials: Array.isArray(contentsFormData.roofMaterials)
          ? contentsFormData.roofMaterials
          : contentsFormData.roofMaterials ? [contentsFormData.roofMaterials] : [],
        floorMaterials: Array.isArray(contentsFormData.floorMaterials)
          ? contentsFormData.floorMaterials
          : contentsFormData.floorMaterials ? [contentsFormData.floorMaterials] : [],
        otherSurroundingArea: Array.isArray(contentsFormData.otherSurroundingArea)
          ? contentsFormData.otherSurroundingArea
          : contentsFormData.otherSurroundingArea ? [contentsFormData.otherSurroundingArea] : [],
        
        id: isEditMode && contents?.id ? contents.id : `temp-${Date.now()}`,
        yearsLivedOnProperty: Number(contentsFormData.yearsLivedOnProperty),
        insuredValue: Number(contentsFormData.insuredValue),
        premium: contentsFormData.premium ? Number(contentsFormData.premium) : undefined,
        excess: contentsFormData.excess ? Number(contentsFormData.excess) : undefined,
        
        // ← This is the key line that fixes the logic
        isTemp: !shouldSaveToDatabaseNow,   // true = still draft / temp, false = persisted
        quoteId: hasRealQuoteId ? Number(quoteId) : undefined,
      };

      console.log('📦 Saving household contents (local):', contentsForState);

      let savedContents = contentsForState;

      // ──────────────────────────────────────────────────────────────
      // Step 3: Only save to DB if we decided it's appropriate
      // ──────────────────────────────────────────────────────────────
      if (shouldSaveToDatabaseNow) {
        console.log("→ Saving / updating household contents in DATABASE (existing quote)");

        // your existing snake_case conversion
        const dbData = {
          years_lived_on_property: contentsForState.yearsLivedOnProperty,
          burglar_bar_type: contentsForState.burglarBarType,
          day_time_occupant_type: contentsForState.dayTimeOccupantType,
          cover_type: contentsForState.coverType,
          insured_value: contentsForState.insuredValue,
          conducts_business: contentsForState.conductsBusiness,
          has_surveillance_camera: contentsForState.hasSurveillanceCamera,
          has_external_sliding_door: contentsForState.hasExternalSlidingDoor,
          has_security_gate: contentsForState.hasSecurityGate,
          in_security_complex: contentsForState.inSecurityComplex,
          has_alarm_records: contentsForState.hasAlarmRecords,
          has_neighborhood_watch: contentsForState.hasNeighborhoodWatch,
          alarm_system_type: contentsForState.alarmSystemType,
          has_accidental_damage_cover: contentsForState.hasAccidentalDamageCover,
          accidental_damage_value: contentsForState.accidentalDamageValue || 0,
          has_power_surge_cover: contentsForState.hasPowerSurgeCover,
          power_surge_value: contentsForState.powerSurgeValue || 0,
          address_line1: contentsForState.addressLine1,
          address_line2: contentsForState.addressLine2,
          suburb: contentsForState.suburb,
          city_town: contentsForState.cityTown,
          postal_code: contentsForState.postalCode,
          region: contentsForState.region,
          country: contentsForState.country,
          property_type: contentsForState.propertyType,
          residence_type: contentsForState.residenceType,
          building_age: contentsForState.buildingAge,
          situated_type: contentsForState.situatedType,
          has_swimming_pool: contentsForState.hasSwimmingPool,
          has_motorized_gate: contentsForState.hasMotorizedGate,
          is_property_unoccupied: contentsForState.isPropertyUnoccupied,
          is_commune: contentsForState.isCommune,
          under_construction: contentsForState.underConstruction,
          built_on_steep_slope: contentsForState.builtOnSteepSlope,
          has_retaining_walls: contentsForState.hasRetainingWalls,
          near_water_body: contentsForState.nearWaterBody,
          has_rising_damp: contentsForState.hasRisingDamp,
          has_wall_cracks: contentsForState.hasWallCracks,
          history_of_landslip: contentsForState.historyOfLandslip,
          has_outbuilding: contentsForState.hasOutbuilding,
          has_perimeter_fence: contentsForState.hasPerimeterFence,
          has_electric_fence: contentsForState.hasElectricFence,
          has_thatch_roof: contentsForState.hasThatchRoof,
          perimeter_type: Array.isArray(contentsForState.perimeterType)
            ? contentsForState.perimeterType.join(', ')
            : '',
          wall_materials: Array.isArray(contentsForState.wallMaterials)
            ? contentsForState.wallMaterials.join(', ')
            : '',
          roof_materials: Array.isArray(contentsForState.roofMaterials)
            ? contentsForState.roofMaterials.join(', ')
            : '',
          floor_materials: Array.isArray(contentsForState.floorMaterials)
            ? contentsForState.floorMaterials.join(', ')
            : '',
          other_surrounding_area: Array.isArray(contentsForState.otherSurroundingArea)
            ? contentsForState.otherSurroundingArea.join(', ')
            : '',
          premium: contentsForState.premium,
          excess: contentsForState.excess,
          no_claim_bonus: contentsForState.noClaimBonus
        };

        const numericQuoteId = Number(quoteId);

        const dbResult = await saveHouseholdContentsToDB(dbData, numericQuoteId);

        if (dbResult && dbResult.id) {
          savedContents = {
            ...contentsForState,
            id: dbResult.id,
            isTemp: false,
            quoteId: numericQuoteId
          };
          console.log("→ Got real ID from DB:", dbResult.id);
        }
      } else {
        console.log("→ Keeping household contents TEMPORARY only (no DB save yet)");
      }

      // ──────────────────────────────────────────────────────────────
      // Step 4: Always update parent / local state
      // ──────────────────────────────────────────────────────────────
      onSave(savedContents);

      handleSetSaveStatus('success');
      handleSetSaveMessage(
        shouldSaveToDatabaseNow
          ? "Household contents saved successfully ✓"
          : "Added to quote draft (will save when quote is created)"
      );

      // Close modal after short delay
      setTimeout(() => {
        handleClose();
      }, 1200);

    } catch (error) {
      console.error('❌ Error in handleSubmit:', error);
      handleSetSaveStatus('error');
      handleSetSaveMessage(
        `Failed to ${isEditMode ? 'update' : 'add'} household contents: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  };

  const handleClose = () => {
    console.log('🔒 Household Contents Modal closing, resetting form');
    setContentsFormData(getInitialFormData());
    setContentsErrors({
      yearsLivedOnProperty: '',
      burglarBarType: '',
      dayTimeOccupantType: '',
      coverType: '',
      insuredValue: '',
      addressLine1: '',
      suburb: '',
      cityTown: '',
      postalCode: '',
      region: '',
      country: '',
      server: '',
    });
    setCurrencyDisplay({
      insuredValue: '',
      premium: '',
      excess: '',
    });
    
    // Reset all status states
    handleSetSaveStatus('idle');
    handleSetSaveMessage('');
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div className={`${isVisible ? 'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50' : 'hidden'}`}>
      <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">
            {isViewMode ? 'View Household Contents' : 
             isEditMode ? 'Edit Household Contents' : 'Add Household Contents'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">&times;</button>
        </div>

        {/* FIXED: Use internal state for display */}
        {saveMessageInternal && (
          <div className={`${saveMessageInternal.includes('❌') ? 'bg-red-100 border-red-400 text-red-700' : 'bg-green-100 border-green-400 text-green-700'} border px-4 py-3 rounded relative mb-4`}>
            {saveMessageInternal}
          </div>
        )}
        {saveStatusInternal === 'error' && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
            {saveMessageInternal}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
            {/* Years Lived on Property */}
            <div className="flex flex-col">
                <label htmlFor="yearsLivedOnProperty" className="text-sm font-medium mb-1">
                    Years Lived on Property
                </label>
                <input
                  id="yearsLivedOnProperty"
                  name="yearsLivedOnProperty"
                  type="number"
                  value={contentsFormData.yearsLivedOnProperty ?? ''}
                  onChange={handleInputChange}
                  className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ 
                    WebkitAppearance: 'textfield',
                    MozAppearance: 'textfield',
                    appearance: 'textfield'
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  disabled={mode === 'view'}
                />
                {contentsErrors.yearsLivedOnProperty && (
                    <p className="text-red-500 text-sm mt-1">{contentsErrors.yearsLivedOnProperty}</p>
                )}
                </div>

            {/* Burglar Bar Type */}
            <div className="flex flex-col">
              <label htmlFor="burglarBarType" className="text-sm font-medium mb-1">
                Burglar Bar Type
              </label>
              <select
                id="burglarBarType"
                name="burglarBarType"
                value={contentsFormData.burglarBarType}
                onChange={handleInputChange}
                className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={mode === 'view'}
              >
                <option value="">Select burglar bar type</option>
                <option value="None">None</option>
                <option value="All Windows">All Windows</option>
                <option value="Opening Windows Only">Opening Windows Only</option>
              </select>
              {contentsErrors.burglarBarType && (
                <p className="text-red-500 text-sm mt-1">{contentsErrors.burglarBarType}</p>
              )}
            </div>

            {/* Day Time Occupant Type */}
            <div className="flex flex-col">
              <label htmlFor="dayTimeOccupantType" className="text-sm font-medium mb-1">
                Day Time Occupant Type
              </label>
              <select
                id="dayTimeOccupantType"
                name="dayTimeOccupantType"
                value={contentsFormData.dayTimeOccupantType}
                onChange={handleInputChange}
                className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isViewMode}
              >
                <option value="">Select occupant type</option>
                <option value="none">None</option>
                <option value="household_member">Household Member</option>
                <option value="domestic_employee">Domestic Employee</option>
                <option value="tenant">Tenant</option>
                <option value="insured">Insured</option>
                <option value="outstanding">Outstanding</option>
                <option value="family_member">Family Member</option>
                <option value="other">Other</option>
              </select>
              {contentsErrors.dayTimeOccupantType && (
                <p className="text-red-500 text-sm mt-1">{contentsErrors.dayTimeOccupantType}</p>
              )}
            </div>
            </div>

            {/* Cover Type, Insured Value, and No Claim Bonus */}
            <div className="grid grid-cols-3 gap-4">
              {/* Cover Type - MOVED HERE */}
              <div className="flex flex-col">
                <label htmlFor="coverType" className="text-sm font-medium mb-1">
                  Cover Type
                </label>
                <select
                  id="coverType"
                  name="coverType"
                  value={contentsFormData.coverType}
                  onChange={handleInputChange}
                  className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isViewMode}
                >
                  <option value="">Select cover type</option>
                  <option value="Comprehensive">Comprehensive</option>
                  <option value="Theft Cover Excluding">Theft Cover Excluding</option>
                  <option value="Flood Cover Excluding">Flood Cover Excluding</option>
                  <option value="Advensure">Advensure</option>
                </select>
                {contentsErrors.coverType && (
                  <p className="text-red-500 text-sm mt-1">{contentsErrors.coverType}</p>
                )}
              </div>

              {/* Insured Value */}
              <div className="flex flex-col">
                <label htmlFor="insuredValue" className="text-sm font-medium mb-1">
                  Insured Value <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500">R</span>
                  </div>
                  <input
                    id="insuredValue"
                    name="insuredValue"
                    type="text"
                    value={currencyDisplay.insuredValue}
                    onChange={(e) => handleCurrencyChange(e, 'insuredValue')}
                    className="border border-gray-300 rounded-md p-2 pl-8 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                    placeholder="0.00"
                    disabled={isViewMode}
                    onBlur={(e) => {
                      // Ensure proper formatting on blur
                      const numericValue = parseCurrencyInput(e.target.value);
                      if (numericValue > 0) {
                        const formatted = formatCurrencyInput(numericValue.toFixed(2));
                        setCurrencyDisplay(prev => ({ ...prev, insuredValue: formatted }));
                      }
                    }}
                  />
                </div>
                {contentsErrors.insuredValue && (
                  <p className="text-red-500 text-sm mt-1">{contentsErrors.insuredValue}</p>
                )}
              </div>

              {/* No Claim Bonus */}
              <div className="flex flex-col">
                <label htmlFor="noClaimBonus" className="text-sm font-medium mb-1">
                  No Claim Bonus
                </label>
                <select
                  id="noClaimBonus"
                  name="noClaimBonus"
                  value={contentsFormData.noClaimBonus}
                  onChange={handleInputChange}
                  className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isViewMode}
                >
                  <option value="">Select no claim bonus</option>
                  <option value="12 Months  (1 Year)">12 Months  (1 Year)</option>
                  <option value="24 Months  (2 Year)">24 Months  (2 Year)</option>
                  <option value="36 Months  (3 Year)">36 Months  (3 Year)</option>
                  <option value="48 Months  (4 Year)">48 Months  (4 Year)</option>
                  <option value="60 Months  (5 Year)">60 Months  (5 Year)</option>
                  <option value="72 Months  (6 Year)">72 Months  (6 Year)</option>
                  <option value="84 Months  (7 Year)">84 Months  (7 Year)</option>
                  <option value="96 Months  (8 Year)">96 Months  (8 Year)</option>
                  <option value="108 Months  (9 Year)">108 Months  (9 Year)</option>
                  <option value="120Months  (10 Year)">120 Months  (10 Year)</option>
                </select>
              </div>
            </div>

            {/* Security Features Checkboxes */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-semibold mb-3">Security Features</h3>
              
              <div className="grid grid-cols-2 gap-4">
                {/* First column of checkboxes */}
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      id="conductsBusiness"
                      name="conductsBusiness"
                      type="checkbox"
                      checked={contentsFormData.conductsBusiness || false}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      disabled={mode === 'view'}
                    />
                    <label htmlFor="conductsBusiness" className="ml-2 text-sm text-gray-700">
                      Does the resident conduct a business on the premises
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="hasSurveillanceCamera"
                      name="hasSurveillanceCamera"
                      type="checkbox"
                      checked={contentsFormData.hasSurveillanceCamera || false}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      disabled={isViewMode}
                    />
                    <label htmlFor="hasSurveillanceCamera" className="ml-2 text-sm text-gray-700">
                      Has surveillance camera
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="hasExternalSlidingDoor"
                      name="hasExternalSlidingDoor"
                      type="checkbox"
                      checked={contentsFormData.hasExternalSlidingDoor || false}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      disabled={isViewMode}
                    />
                    <label htmlFor="hasExternalSlidingDoor" className="ml-2 text-sm text-gray-700">
                      Has external sliding door
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="hasSecurityGate"
                      name="hasSecurityGate"
                      type="checkbox"
                      checked={contentsFormData.hasSecurityGate || false}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      disabled={isViewMode}
                    />
                    <label htmlFor="hasSecurityGate" className="ml-2 text-sm text-gray-700">
                      Has security gate on external doors
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="inSecurityComplex"
                      name="inSecurityComplex"
                      type="checkbox"
                      checked={contentsFormData.inSecurityComplex || false}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      disabled={isViewMode}
                    />
                    <label htmlFor="inSecurityComplex" className="ml-2 text-sm text-gray-700">
                      Is in a security complex
                    </label>
                  </div>
                </div>
                
                {/* Second column of checkboxes */}
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      id="hasAlarmRecords"
                      name="hasAlarmRecords"
                      type="checkbox"
                      checked={contentsFormData.hasAlarmRecords || false}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      disabled={isViewMode}
                    />
                    <label htmlFor="hasAlarmRecords" className="ml-2 text-sm text-gray-700">
                      Has 2 month alarm triggered records
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="hasNeighborhoodWatch"
                      name="hasNeighborhoodWatch"
                      type="checkbox"
                      checked={contentsFormData.hasNeighborhoodWatch || false}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      disabled={isViewMode}
                    />
                    <label htmlFor="hasNeighborhoodWatch" className="ml-2 text-sm text-gray-700">
                      Has an active neighbourhood watch
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="hasAccidentalDamageCover"
                      name="hasAccidentalDamageCover"
                      type="checkbox"
                      checked={contentsFormData.hasAccidentalDamageCover || false}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      disabled={isViewMode}
                    />
                    <label htmlFor="hasAccidentalDamageCover" className="ml-2 text-sm text-gray-700">
                      Accidental damage cover
                    </label>
                  </div>

                  {/* NEW: Conditional input for Accidental Damage value */}
                  {contentsFormData.hasAccidentalDamageCover && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Accidental Damage Cover Sum Insured
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500">R</span>
                        </div>
                        <input
                          type="text"
                          value={accidentalDamageDisplay}
                          onChange={handleAccidentalDamageChange}
                          className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full"
                          placeholder="0.00"
                          disabled={isViewMode}
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center">
                    <input
                      id="hasPowerSurgeCover"
                      name="hasPowerSurgeCover"
                      type="checkbox"
                      checked={contentsFormData.hasPowerSurgeCover || false}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      disabled={isViewMode}
                    />
                    <label htmlFor="hasPowerSurgeCover" className="ml-2 text-sm text-gray-700">
                      Power surge cover
                    </label>
                  </div>

                  {/* NEW: Conditional input for Power Surge value */}
                  {contentsFormData.hasPowerSurgeCover && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Power Surge Cover Sum Insured
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500">R</span>
                        </div>
                        <input
                          type="text"
                          value={powerSurgeDisplay}
                          onChange={handlePowerSurgeChange}
                          className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full"
                          placeholder="0.00"
                          disabled={isViewMode}
                        />
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-gray-200">
              {/* Alarm System Type */}
              <div className="flex flex-col">
                <label htmlFor="alarmSystemType" className="text-sm font-medium mb-1">
                  What type of alarm system does the property have?
                </label>
                <select
                  id="alarmSystemType"
                  name="alarmSystemType"
                  value={contentsFormData.alarmSystemType}
                  onChange={handleInputChange}
                  className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isViewMode}
                >
                  <option value="">Select alarm system type</option>
                  <option value="none">None</option>
                  <option value="alarm">Alarm</option>
                  <option value="radio_linked_alarm">Radio Linked Alarm</option>
                  <option value="24_hour_armed_response">24 Hour Armed Response</option>
                  <option value="self_maintenance_alarm">Self Maintenance Alarm</option>
                </select>
              </div>

            </div>

            {/* Address Section */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              {isCreateMode && (
              <div className="mb-4">
                <button
                  type="button"
                  onClick={handleFillFromClient}
                  disabled={isFetchingAddress}
                  className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 disabled:bg-gray-300"
                >
                  {isFetchingAddress ? 'Fetching...' : 'Fill from Client'}
                </button>
              </div>
            )}
              
              <div className="grid grid-cols-2 gap-4">
                {/* Address Line 1 */}
                <div className="flex flex-col">
                  <label htmlFor="addressLine1" className="text-sm font-medium mb-1">
                    Address Line 1
                  </label>
                  <input
                    id="addressLine1"
                    name="addressLine1"
                    type="text"
                    value={contentsFormData.addressLine1 || ''}
                    onChange={handleInputChange}
                    className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isViewMode}
                  />
                </div>

                {/* Address Line 2 */}
                <div className="flex flex-col">
                  <label htmlFor="addressLine2" className="text-sm font-medium mb-1">
                    Address Line 2
                  </label>
                  <input
                    id="addressLine2"
                    name="addressLine2"
                    type="text"
                    value={contentsFormData.addressLine2 || ''}
                    onChange={handleInputChange}
                    className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isViewMode}
                  />
                </div>

                {/* Suburb */}
                <div className="flex flex-col">
                  <label htmlFor="suburb" className="text-sm font-medium mb-1">
                    Suburb
                  </label>
                  <input
                    id="suburb"
                    name="suburb"
                    type="text"
                    value={contentsFormData.suburb || ''}
                    onChange={handleInputChange}
                    className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isViewMode}
                  />
                </div>

                {/* City/Town */}
                <div className="flex flex-col">
                  <label htmlFor="cityTown" className="text-sm font-medium mb-1">
                    City/Town
                  </label>
                  <input
                    id="cityTown"
                    name="cityTown"
                    type="text"
                    value={contentsFormData.cityTown || ''}
                    onChange={handleInputChange}
                    className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isViewMode}
                  />
                </div>

                {/* Postal Code */}
                <div className="flex flex-col">
                  <label htmlFor="postalCode" className="text-sm font-medium mb-1">
                    Postal Code
                  </label>
                  <input
                    id="postalCode"
                    name="postalCode"
                    type="text"
                    value={contentsFormData.postalCode || ''}
                    onChange={handleInputChange}
                    className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isViewMode}
                  />
                </div>

                {/* Region */}
                <div className="flex flex-col">
                  <label htmlFor="region" className="text-sm font-medium mb-1">
                    Region
                  </label>
                  <input
                    id="region"
                    name="region"
                    type="text"
                    value={contentsFormData.region || ''}
                    onChange={handleInputChange}
                    className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isViewMode}
                  />
                </div>

                {/* Country */}
                <div className="flex flex-col col-span-2">
                  <label htmlFor="country" className="text-sm font-medium mb-1">
                    Country
                  </label>
                  <input
                    id="country"
                    name="country"
                    type="text"
                    value={contentsFormData.country || ''}
                    onChange={handleInputChange}
                    className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isViewMode}
                  />
                </div>
              </div>
            </div>

            {/* Property Details Section */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-semibold mb-3">Property Details</h3>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Property Type */}
                <div className="flex flex-col">
                  <label htmlFor="propertyType" className="text-sm font-medium mb-1">
                    Property Type
                  </label>
                  <select
                    id="propertyType"
                    name="propertyType"
                    value={contentsFormData.propertyType}
                    onChange={handleInputChange}
                    className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isViewMode}
                  >
                    <option value="">Select property type</option>
                    <option value="house">House</option>
                    <option value="duplex">Duplex</option>
                    <option value="townhouse">Townhouse</option>
                    <option value="apartment">Apartment</option>
                    <option value="condo">Condo</option>
                    <option value="villa">Villa</option>
                    <option value="cluster">Cluster</option>
                    <option value="cottage_attached">Cottage Attached</option>
                    <option value="cottage_not_attached">Cottage Not Attached</option>
                    <option value="flat_above_ground">Flat Above Ground</option>
                    <option value="flat_on_ground">Flat On Ground</option>
                    <option value="outbuilding">Outbuilding</option>
                    <option value="park_home">Park Home / Caravan</option>
                    <option value="plot">Plot</option>
                    <option value="established">Established</option>
                    <option value="new_extension">New Extension</option>
                    <option value="farm">Farm</option>
                    <option value="informal_settlement">Informal Settlement</option>
                    <option value="commercial">Commercial</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Residence Type */}
                <div className="flex flex-col">
                  <label htmlFor="residenceType" className="text-sm font-medium mb-1">
                    Residence Type
                  </label>
                  <select
                    id="residenceType"
                    name="residenceType"
                    value={contentsFormData.residenceType}
                    onChange={handleInputChange}
                    className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isViewMode}
                  >
                    <option value="">Select residence type</option>
                    <option value="primary">Primary Residence</option>
                    <option value="secondary">Secondary Residence</option>
                    <option value="holiday">Holiday Residence</option>
                    <option value="rental">Rental / Tenant Occupied</option>
                    <option value="vacant">Vacant / Unoccupied</option>
                    <option value="business">Business / Commercial</option>
                    <option value="mixed">Mixed Use</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Building Age */}
                <div className="flex flex-col">
                  <label htmlFor="buildingAge" className="text-sm font-medium mb-1">
                    Building Age
                  </label>
                  <select
                    id="buildingAge"
                    name="buildingAge"
                    value={contentsFormData.buildingAge}
                    onChange={handleInputChange}
                    className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isViewMode}
                  >
                    <option value="">Select building age</option>
                    <option value="0-5">0-5 Years</option>
                    <option value="6-10">6-10 Years</option>
                    <option value="11-25">11-25 Years</option>
                    <option value="25+">Older Than 25 Years</option>
                  </select>
                </div>

                {/* Situated Type */}
                <div className="flex flex-col">
                  <label htmlFor="situatedType" className="text-sm font-medium mb-1">
                    Situated Type
                  </label>
                  <select
                    id="situatedType"
                    name="situatedType"
                    value={contentsFormData.situatedType}
                    onChange={handleInputChange}
                    className="border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isViewMode}
                  >
                    <option value="">Select situated type</option>
                    <option value="urban">Urban</option>
                    <option value="suburban">Suburban</option>
                    <option value="rural">Rural</option>
                    <option value="coastal">Coastal</option>
                    <option value="mountain">Mountain</option>
                    <option value="island">Island</option>
                    <option value="industrial">Industrial</option>
                    <option value="commercial">Commercial</option>
                    <option value="mixed">Mixed Use</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {/* Property Features Checkboxes */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="text-md font-semibold mb-3">Property Features</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* First column of checkboxes */}
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        id="hasSwimmingPool"
                        name="hasSwimmingPool"
                        type="checkbox"
                        checked={contentsFormData.hasSwimmingPool || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        disabled={isViewMode}
                      />
                      <label htmlFor="hasSwimmingPool" className="ml-2 text-sm text-gray-700">
                        Has swimming pool
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="hasMotorizedGate"
                        name="hasMotorizedGate"
                        type="checkbox"
                        checked={contentsFormData.hasMotorizedGate || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        disabled={isViewMode}
                      />
                      <label htmlFor="hasMotorizedGate" className="ml-2 text-sm text-gray-700">
                        Has motorized gate
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="isPropertyUnoccupied"
                        name="isPropertyUnoccupied"
                        type="checkbox"
                        checked={contentsFormData.isPropertyUnoccupied || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        disabled={isViewMode}
                      />
                      <label htmlFor="isPropertyUnoccupied" className="ml-2 text-sm text-gray-700">
                        Is property unoccupied for more than 60 consecutive days per annum
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="isCommune"
                        name="isCommune"
                        type="checkbox"
                        checked={contentsFormData.isCommune || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        disabled={isViewMode}
                      />
                      <label htmlFor="isCommune" className="ml-2 text-sm text-gray-700">
                        Is commune
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="underConstruction"
                        name="underConstruction"
                        type="checkbox"
                        checked={contentsFormData.underConstruction || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        disabled={isViewMode}
                      />
                      <label htmlFor="underConstruction" className="ml-2 text-sm text-gray-700">
                        Under construction
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="builtOnSteepSlope"
                        name="builtOnSteepSlope"
                        type="checkbox"
                        checked={contentsFormData.builtOnSteepSlope || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        disabled={isViewMode}
                      />
                      <label htmlFor="builtOnSteepSlope" className="ml-2 text-sm text-gray-700">
                        Built on steep slope
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="hasRetainingWalls"
                        name="hasRetainingWalls"
                        type="checkbox"
                        checked={contentsFormData.hasRetainingWalls || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        disabled={isViewMode}
                      />
                      <label htmlFor="hasRetainingWalls" className="ml-2 text-sm text-gray-700">
                        Has the property any excessive use of retaining walls
                      </label>
                    </div>
                  </div>
                  
                  {/* Second column of checkboxes */}
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        id="nearWaterBody"
                        name="nearWaterBody"
                        type="checkbox"
                        checked={contentsFormData.nearWaterBody || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        disabled={isViewMode}
                      />
                      <label htmlFor="nearWaterBody" className="ml-2 text-sm text-gray-700">
                        Less than 100 m from Sea, Lagoon, River, Natural Stream, Dam or Swamp
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="hasRisingDamp"
                        name="hasRisingDamp"
                        type="checkbox"
                        checked={contentsFormData.hasRisingDamp || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        disabled={isViewMode}
                      />
                      <label htmlFor="hasRisingDamp" className="ml-2 text-sm text-gray-700">
                        Has the property any visible signs of rising damp
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="hasWallCracks"
                        name="hasWallCracks"
                        type="checkbox"
                        checked={contentsFormData.hasWallCracks || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        disabled={isViewMode}
                      />
                      <label htmlFor="hasWallCracks" className="ml-2 text-sm text-gray-700">
                        Has the property any visible signs of cracks in the wall
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="historyOfLandslip"
                        name="historyOfLandslip"
                        type="checkbox"
                        checked={contentsFormData.historyOfLandslip || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        disabled={isViewMode}
                      />
                      <label htmlFor="historyOfLandslip" className="ml-2 text-sm text-gray-700">
                        History of landslip or Subsidence in the area
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="hasOutbuilding"
                        name="hasOutbuilding"
                        type="checkbox"
                        checked={contentsFormData.hasOutbuilding || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        disabled={isViewMode}
                      />
                      <label htmlFor="hasOutbuilding" className="ml-2 text-sm text-gray-700">
                        Has outbuilding
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="hasPerimeterFence"
                        name="hasPerimeterFence"
                        type="checkbox"
                        checked={contentsFormData.hasPerimeterFence || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        disabled={isViewMode}
                      />
                      <label htmlFor="hasPerimeterFence" className="ml-2 text-sm text-gray-700">
                        Property surrounded by perimeter fence at least 1.8m high
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="hasElectricFence"
                        name="hasElectricFence"
                        type="checkbox"
                        checked={contentsFormData.hasElectricFence || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        disabled={isViewMode}
                      />
                      <label htmlFor="hasElectricFence" className="ml-2 text-sm text-gray-700">
                        Has electric fence on perimeter
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="hasThatchRoof"
                        name="hasThatchRoof"
                        type="checkbox"
                        checked={contentsFormData.hasThatchRoof || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        disabled={isViewMode}
                      />
                      <label htmlFor="hasThatchRoof" className="ml-2 text-sm text-gray-700">
                        Thatch roof present
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Material Details Section */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="text-md font-semibold mb-3">Material Details</h4>
                
                <div className="space-y-6">

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Perimeter Type
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {perimeterTypeOptions.map(option => (
                        <label key={option} className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={(contentsFormData.perimeterType || []).includes(option)}
                            onChange={(e) => handleCheckboxGroupChange('perimeterType', option, e.target.checked)}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 mr-2"
                            disabled={isViewMode}
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      {contentsFormData.perimeterType && contentsFormData.perimeterType.length > 0 ? (
                        <p className="text-xs text-green-600">
                          Selected: {contentsFormData.perimeterType.join(', ')}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500">No selections</p>
                      )}
                      <button
                        type="button"
                        onClick={() => clearAllSelections('perimeterType')}
                        className="text-xs text-red-500 hover:text-red-700 disabled:text-gray-300"
                        disabled={isViewMode || (contentsFormData.perimeterType?.length || 0) === 0}
                      >
                        Clear All
                      </button>
                    </div>
                  </div>

                  {/* Wall Materials */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Wall Materials
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {wallMaterialsOptions.map(option => (
                        <label key={option} className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={(contentsFormData.wallMaterials || []).includes(option)}
                            onChange={(e) => handleCheckboxGroupChange('wallMaterials', option, e.target.checked)}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 mr-2"
                            disabled={isViewMode}
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      {contentsFormData.wallMaterials && contentsFormData.wallMaterials.length > 0 ? (
                        <p className="text-xs text-green-600">
                          Selected: {contentsFormData.wallMaterials.join(', ')}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500">No selections</p>
                      )}
                      <button
                        type="button"
                        onClick={() => clearAllSelections('wallMaterials')}
                        className="text-xs text-red-500 hover:text-red-700 disabled:text-gray-300"
                        disabled={isViewMode || (contentsFormData.wallMaterials?.length || 0) === 0}
                      >
                        Clear All
                      </button>
                    </div>
                  </div>

                  {/* Roof Materials */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Roof Materials
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {roofMaterialsOptions.map(option => (
                        <label key={option} className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={(contentsFormData.roofMaterials || []).includes(option)}
                            onChange={(e) => handleCheckboxGroupChange('roofMaterials', option, e.target.checked)}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 mr-2"
                            disabled={isViewMode}
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      {contentsFormData.roofMaterials && contentsFormData.roofMaterials.length > 0 ? (
                        <p className="text-xs text-green-600">
                          Selected: {contentsFormData.roofMaterials.join(', ')}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500">No selections</p>
                      )}
                      <button
                        type="button"
                        onClick={() => clearAllSelections('roofMaterials')}
                        className="text-xs text-red-500 hover:text-red-700 disabled:text-gray-300"
                        disabled={isViewMode || (contentsFormData.roofMaterials?.length || 0) === 0}
                      >
                        Clear All
                      </button>
                    </div>
                  </div>

                  {/* Floor Materials */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Floor Materials
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {floorMaterialsOptions.map(option => (
                        <label key={option} className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={(contentsFormData.floorMaterials || []).includes(option)}
                            onChange={(e) => handleCheckboxGroupChange('floorMaterials', option, e.target.checked)}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 mr-2"
                            disabled={isViewMode}
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      {contentsFormData.floorMaterials && contentsFormData.floorMaterials.length > 0 ? (
                        <p className="text-xs text-green-600">
                          Selected: {contentsFormData.floorMaterials.join(', ')}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500">No selections</p>
                      )}
                      <button
                        type="button"
                        onClick={() => clearAllSelections('floorMaterials')}
                        className="text-xs text-red-500 hover:text-red-700 disabled:text-gray-300"
                        disabled={isViewMode || (contentsFormData.floorMaterials?.length || 0) === 0}
                      >
                        Clear All
                      </button>
                    </div>
                  </div>

                  {/* Other Surrounding Area */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Other Surrounding Area
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {surroundingAreaOptions.map(option => (
                        <label key={option} className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={(contentsFormData.otherSurroundingArea || []).includes(option)}
                            onChange={(e) => handleCheckboxGroupChange('otherSurroundingArea', option, e.target.checked)}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 mr-2"
                            disabled={isViewMode}
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      {contentsFormData.otherSurroundingArea && contentsFormData.otherSurroundingArea.length > 0 ? (
                        <p className="text-xs text-green-600">
                          Selected: {contentsFormData.otherSurroundingArea.join(', ')}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500">No selections</p>
                      )}
                      <button
                        type="button"
                        onClick={() => clearAllSelections('otherSurroundingArea')}
                        className="text-xs text-red-500 hover:text-red-700 disabled:text-gray-300"
                        disabled={isViewMode || (contentsFormData.otherSurroundingArea?.length || 0) === 0}
                      >
                        Clear All
                      </button>
                    </div>
                  </div>

                </div>
              </div>

                {/* Financial Details Section */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="text-md font-semibold mb-3">Financial Details</h4>
                
                {/* Use flex instead of grid to make them fill available space */}
                <div className="flex space-x-4">
                  {/* Premium - now uses flex-1 to fill available space */}
                  <div className="flex-1">
                    <label htmlFor="premium" className="text-sm font-medium mb-1">
                      Premium
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">R</span>
                      </div>
                      <input
                        id="premium"
                        name="premium"
                        type="text"
                        value={currencyDisplay.premium}
                        onChange={(e) => handleCurrencyChange(e, 'premium')}
                        className="border border-gray-300 rounded-md p-2 pl-8 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                        placeholder="0.00"
                        disabled={isViewMode}
                        onBlur={(e) => {
                          const numericValue = parseCurrencyInput(e.target.value);
                          if (numericValue > 0) {
                            const formatted = formatCurrencyInput(numericValue.toFixed(2));
                            setCurrencyDisplay(prev => ({ ...prev, premium: formatted }));
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Excess - now uses flex-1 to fill available space */}
                  <div className="flex-1">
                    <label htmlFor="excess" className="text-sm font-medium mb-1">
                      Excess <span className="font-bold italic text-gray-600">(Rand Value Only)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500">R</span>
                      </div>
                      <input
                        id="excess"
                        name="excess"
                        type="text"
                        value={currencyDisplay.excess}
                        onChange={(e) => handleCurrencyChange(e, 'excess')}
                        className="border border-gray-300 rounded-md p-2 pl-8 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                        placeholder="0.00"
                        disabled={isViewMode}
                        onBlur={(e) => {
                          const numericValue = parseCurrencyInput(e.target.value);
                          if (numericValue > 0) {
                            const formatted = formatCurrencyInput(numericValue.toFixed(2));
                            setCurrencyDisplay(prev => ({ ...prev, excess: formatted }));
                          }
                        }}
                      />
                    </div>
                  </div>

                </div>
              </div>
            </div>

          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              {isViewMode ? 'Close' : 'Cancel'}
            </button>
            {!isViewMode && (
              <button
                type="submit"
                disabled={saveStatusInternal === 'loading'}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed"
              >
                {saveStatusInternal === 'loading' ? 'Saving...' : 
                isEditMode ? 'Update Contents' : 'Save Contents'}
              </button>
            )}
          </div>

        </form>
      </div>
    </div>
  );
};

  const EditableHouseholdContentsCard: React.FC<EditableHouseholdContentsCardProps> = ({
  contents, 
  index, 
  onSave, 
  onCancel, 
  onEdit,
  cardNumber,
  cardTitle = 'Household Contents',
  showCardNumber = true
}) => {
  const [editForm, setEditForm] = React.useState({
    yearsLivedOnProperty: contents.yearsLivedOnProperty?.toString() || '',
    burglarBarType: contents.burglarBarType || '',
    dayTimeOccupantType: contents.dayTimeOccupantType || '',
    coverType: contents.coverType || '',
    insuredValue: contents.insuredValue?.toString() || '',
    conductsBusiness: contents.conductsBusiness || false,
    hasSurveillanceCamera: contents.hasSurveillanceCamera || false,
    hasExternalSlidingDoor: contents.hasExternalSlidingDoor || false,
    hasSecurityGate: contents.hasSecurityGate || false,
    inSecurityComplex: contents.inSecurityComplex || false,
    hasAlarmRecords: contents.hasAlarmRecords || false,
    hasNeighborhoodWatch: contents.hasNeighborhoodWatch || false,
    hasAccidentalDamageCover: contents.hasAccidentalDamageCover || false,
    hasPowerSurgeCover: contents.hasPowerSurgeCover || false,
    alarmSystemType: contents.alarmSystemType || '',
    perimeterType: Array.isArray(contents.perimeterType) ? contents.perimeterType : (contents.perimeterType ? [contents.perimeterType] : []),
    addressLine1: contents.addressLine1 || '',
    addressLine2: contents.addressLine2 || '',
    suburb: contents.suburb || '',
    cityTown: contents.cityTown || '',
    postalCode: contents.postalCode || '',
    region: contents.region || '',
    country: contents.country || '',
    propertyType: contents.propertyType || '',
    residenceType: contents.residenceType || '',
    buildingAge: contents.buildingAge || '',
    situatedType: contents.situatedType || '',
    wallMaterials: Array.isArray(contents.wallMaterials) ? contents.wallMaterials : (contents.wallMaterials ? [contents.wallMaterials] : []),
    roofMaterials: Array.isArray(contents.roofMaterials) ? contents.roofMaterials : (contents.roofMaterials ? [contents.roofMaterials] : []),
    floorMaterials: Array.isArray(contents.floorMaterials) ? contents.floorMaterials : (contents.floorMaterials ? [contents.floorMaterials] : []),
    otherSurroundingArea: Array.isArray(contents.otherSurroundingArea) ? contents.otherSurroundingArea : (contents.otherSurroundingArea ? [contents.otherSurroundingArea] : []),
    premium: contents.premium?.toString() || '',
    excess: contents.excess?.toString() || '',
    noClaimBonus: contents.noClaimBonus || '',
  });

  const handleCheckboxChange = (
    field: 'perimeterType' | 'wallMaterials' | 'roofMaterials' | 'floorMaterials' | 'otherSurroundingArea',
    option: string,
    isChecked: boolean
  ) => {
    const currentArray = editForm[field] || [];
    
    if (isChecked) {
      // Add option if checked
      setEditForm(prev => ({
        ...prev,
        [field]: [...currentArray, option]
      }));
    } else {
      // Remove option if unchecked
      setEditForm(prev => ({
        ...prev,
        [field]: currentArray.filter(item => item !== option)
      }));
    }
  };

  const handleEditChange = (field: string, value: string | boolean) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const updatedContents: EditableHouseholdContents = {
      ...contents,
      yearsLivedOnProperty: editForm.yearsLivedOnProperty ? parseInt(editForm.yearsLivedOnProperty) : undefined,
      burglarBarType: editForm.burglarBarType,
      dayTimeOccupantType: editForm.dayTimeOccupantType,
      coverType: editForm.coverType,
      insuredValue: editForm.insuredValue ? parseFloat(editForm.insuredValue) : undefined,
      conductsBusiness: editForm.conductsBusiness,
      hasSurveillanceCamera: editForm.hasSurveillanceCamera,
      hasExternalSlidingDoor: editForm.hasExternalSlidingDoor,
      hasSecurityGate: editForm.hasSecurityGate,
      inSecurityComplex: editForm.inSecurityComplex,
      hasAlarmRecords: editForm.hasAlarmRecords,
      hasNeighborhoodWatch: editForm.hasNeighborhoodWatch,
      hasAccidentalDamageCover: editForm.hasAccidentalDamageCover,
      hasPowerSurgeCover: editForm.hasPowerSurgeCover,
      alarmSystemType: editForm.alarmSystemType,
      perimeterType: editForm.perimeterType,
      addressLine1: editForm.addressLine1,
      addressLine2: editForm.addressLine2,
      suburb: editForm.suburb,
      cityTown: editForm.cityTown,
      postalCode: editForm.postalCode,
      region: editForm.region,
      country: editForm.country,
      propertyType: editForm.propertyType,
      residenceType: editForm.residenceType,
      buildingAge: editForm.buildingAge,
      situatedType: editForm.situatedType,
      wallMaterials: editForm.wallMaterials,
      roofMaterials: editForm.roofMaterials,
      floorMaterials: editForm.floorMaterials,
      otherSurroundingArea: editForm.otherSurroundingArea,
      premium: editForm.premium ? parseFloat(editForm.premium) : undefined,
      excess: editForm.excess ? parseFloat(editForm.excess) : undefined,
      noClaimBonus: editForm.noClaimBonus,
      isEditing: false
    };
    onSave(index, updatedContents);
  };

  // Format currency for display
  const formatCurrency = (value: number | string): string => {
    if (!value) return '';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(numValue) ? '' : `R ${numValue.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format address for display
  const formatAddress = (): string => {
    const addressParts = [
      contents.addressLine1,
      contents.addressLine2,
      contents.suburb,
      contents.cityTown
    ].filter(part => part && part.trim() !== '');
    return addressParts.join(', ');
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="p-4">
        
        <div className="flex justify-between items-start mb-2">
          <h4 className="font-semibold text-green-600">
            {showCardNumber ? `${cardTitle} #${cardNumber || (index + 1)}` : cardTitle}
          </h4>
          <div className="flex items-center space-x-2">
            {!contents.isEditing && (
              <button
                onClick={() => onEdit(index)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Edit
              </button>
            )}
          </div>
        </div>
        
        {contents.isEditing ? (
          <div className="space-y-3">
            {/* Editable Form */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-gray-600 text-sm mb-1">Address Line 1</label>
                <input
                  type="text"
                  value={editForm.addressLine1}
                  onChange={(e) => handleEditChange('addressLine1', e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-gray-600 text-sm mb-1">Suburb</label>
                <input
                  type="text"
                  value={editForm.suburb}
                  onChange={(e) => handleEditChange('suburb', e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                />
              </div>

              <div>
                <label className="block text-gray-600 text-sm mb-1">City</label>
                <input
                  type="text"
                  value={editForm.cityTown}
                  onChange={(e) => handleEditChange('cityTown', e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                />
              </div>

              <div>
                <label className="block text-gray-600 text-sm mb-1">Burglar Bar Type</label>
                <select
                  value={editForm.burglarBarType}
                  onChange={(e) => handleEditChange('burglarBarType', e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="">Select burglar bar type</option>
                  <option value="None">None</option>
                  <option value="All Windows">All Windows</option>
                  <option value="Opening Windows Only">Opening Windows Only</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-600 text-sm mb-1">Property Type</label>
                <select
                  value={editForm.propertyType}
                  onChange={(e) => handleEditChange('propertyType', e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="">Select type</option>
                  <option value="house">House</option>
                  <option value="duplex">Duplex</option>
                  <option value="townhouse">Townhouse</option>
                  <option value="apartment">Apartment</option>
                  <option value="condo">Condo</option>
                  <option value="villa">Villa</option>
                  <option value="cluster">Cluster</option>
                  <option value="cottage_attached">Cottage Attached</option>
                  <option value="cottage_not_attached">Cottage Not Attached</option>
                  <option value="flat_above_ground">Flat Above Ground</option>
                  <option value="flat_on_ground">Flat On Ground</option>
                  <option value="outbuilding">Outbuilding</option>
                  <option value="park_home">Park Home / Caravan</option>
                  <option value="plot">Plot</option>
                  <option value="established">Established</option>
                  <option value="new_extension">New Extension</option>
                  <option value="farm">Farm</option>
                  <option value="informal_settlement">Informal Settlement</option>
                  <option value="commercial">Commercial</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-600 text-sm mb-1">Residence Type</label>
                <select
                  value={editForm.residenceType}
                  onChange={(e) => handleEditChange('residenceType', e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="">Select type</option>
                  <option value="primary">Primary Residence</option>
                  <option value="secondary">Secondary Residence</option>
                  <option value="holiday">Holiday Residence</option>
                  <option value="rental">Rental / Tenant Occupied</option>
                  <option value="vacant">Vacant / Unoccupied</option>
                  <option value="business">Business / Commercial</option>
                  <option value="mixed">Mixed Use</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-600 text-sm mb-1">Building Age</label>
                <select
                  value={editForm.buildingAge}
                  onChange={(e) => handleEditChange('buildingAge', e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="">Select building age</option>
                  <option value="0-5">0-5 Years</option>
                  <option value="6-10">6-10 Years</option>
                  <option value="11-25">11-25 Years</option>
                  <option value="25+">Older Than 25 Years</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-600 text-sm mb-1">Situated Type</label>
                <select
                  value={editForm.situatedType}
                  onChange={(e) => handleEditChange('situatedType', e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="">Select situated type</option>
                  <option value="urban">Urban</option>
                  <option value="suburban">Suburban</option>
                  <option value="rural">Rural</option>
                  <option value="coastal">Coastal</option>
                  <option value="mountain">Mountain</option>
                  <option value="island">Island</option>
                  <option value="industrial">Industrial</option>
                  <option value="commercial">Commercial</option>
                  <option value="mixed">Mixed Use</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-600 text-sm mb-1">Insured Value</label>
                <input
                  type="number"
                  value={editForm.insuredValue}
                  onChange={(e) => handleEditChange('insuredValue', e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-gray-600 text-sm mb-1">Premium</label>
                <input
                  type="number"
                  value={editForm.premium}
                  onChange={(e) => handleEditChange('premium', e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Security Features Checkboxes */}
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editForm.hasSurveillanceCamera}
                  onChange={(e) => handleEditChange('hasSurveillanceCamera', e.target.checked)}
                  className="form-checkbox h-3 w-3 text-blue-600 rounded"
                />
                <label className="ml-2 text-gray-600">Surveillance Camera</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editForm.hasSecurityGate}
                  onChange={(e) => handleEditChange('hasSecurityGate', e.target.checked)}
                  className="form-checkbox h-3 w-3 text-blue-600 rounded"
                />
                <label className="ml-2 text-gray-600">Security Gate</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editForm.inSecurityComplex}
                  onChange={(e) => handleEditChange('inSecurityComplex', e.target.checked)}
                  className="form-checkbox h-3 w-3 text-blue-600 rounded"
                />
                <label className="ml-2 text-gray-600">Security Complex</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editForm.hasAlarmRecords}
                  onChange={(e) => handleEditChange('hasAlarmRecords', e.target.checked)}
                  className="form-checkbox h-3 w-3 text-blue-600 rounded"
                />
                <label className="ml-2 text-gray-600">Alarm Records</label>
              </div>
            </div>

            {/* ADD THIS CODE RIGHT HERE - Material Checkbox Groups */}
            <div className="space-y-4 pt-4 border-t border-gray-200">
              
              {/* Perimeter Type Checkbox Group */}
              <div>
                <label className="block text-gray-600 text-sm mb-1">Perimeter Type</label>
                <div className="flex flex-wrap gap-2">
                  {perimeterTypeOptions.map(option => (
                    <label key={option} className="flex items-center text-xs">
                      <input
                        type="checkbox"
                        checked={(editForm.perimeterType || []).includes(option)}
                        onChange={(e) => handleCheckboxChange('perimeterType', option, e.target.checked)}
                        className="mr-1"
                      />
                      {option}
                    </label>
                  ))}
                </div>
                {(editForm.perimeterType && editForm.perimeterType.length > 0) && (
                  <p className="text-xs text-green-600 mt-1">
                    Selected: {editForm.perimeterType.join(', ')}
                  </p>
                )}
              </div>

              {/* Wall Materials Checkbox Group */}
              <div>
                <label className="block text-gray-600 text-sm mb-1">Wall Materials</label>
                <div className="flex flex-wrap gap-2">
                  {wallMaterialsOptions.map(option => (
                    <label key={option} className="flex items-center text-xs">
                      <input
                        type="checkbox"
                        checked={(editForm.wallMaterials || []).includes(option)}
                        onChange={(e) => handleCheckboxChange('wallMaterials', option, e.target.checked)}
                        className="mr-1"
                      />
                      {option}
                    </label>
                  ))}
                </div>
                {(editForm.wallMaterials && editForm.wallMaterials.length > 0) && (
                  <p className="text-xs text-green-600 mt-1">
                    Selected: {editForm.wallMaterials.join(', ')}
                  </p>
                )}
              </div>

              {/* Roof Materials Checkbox Group */}
              <div>
                <label className="block text-gray-600 text-sm mb-1">Roof Materials</label>
                <div className="flex flex-wrap gap-2">
                  {roofMaterialsOptions.map(option => (
                    <label key={option} className="flex items-center text-xs">
                      <input
                        type="checkbox"
                        checked={(editForm.roofMaterials || []).includes(option)}
                        onChange={(e) => handleCheckboxChange('roofMaterials', option, e.target.checked)}
                        className="mr-1"
                      />
                      {option}
                    </label>
                  ))}
                </div>
                {(editForm.roofMaterials && editForm.roofMaterials.length > 0) && (
                  <p className="text-xs text-green-600 mt-1">
                    Selected: {editForm.roofMaterials.join(', ')}
                  </p>
                )}
              </div>

              {/* Floor Materials Checkbox Group */}
              <div>
                <label className="block text-gray-600 text-sm mb-1">Floor Materials</label>
                <div className="flex flex-wrap gap-2">
                  {floorMaterialsOptions.map(option => (
                    <label key={option} className="flex items-center text-xs">
                      <input
                        type="checkbox"
                        checked={(editForm.floorMaterials || []).includes(option)}
                        onChange={(e) => handleCheckboxChange('floorMaterials', option, e.target.checked)}
                        className="mr-1"
                      />
                      {option}
                    </label>
                  ))}
                </div>
                {(editForm.floorMaterials && editForm.floorMaterials.length > 0) && (
                  <p className="text-xs text-green-600 mt-1">
                    Selected: {editForm.floorMaterials.join(', ')}
                  </p>
                )}
              </div>

              {/* Other Surrounding Area Checkbox Group */}
              <div>
                <label className="block text-gray-600 text-sm mb-1">Surrounding Areas</label>
                <div className="flex flex-wrap gap-2">
                  {surroundingAreaOptions.slice(0, 6).map(option => (
                    <label key={option} className="flex items-center text-xs">
                      <input
                        type="checkbox"
                        checked={(editForm.otherSurroundingArea || []).includes(option)}
                        onChange={(e) => handleCheckboxChange('otherSurroundingArea', option, e.target.checked)}
                        className="mr-1"
                      />
                      {option}
                    </label>
                  ))}
                </div>
                {(editForm.otherSurroundingArea && editForm.otherSurroundingArea.length > 0) && (
                  <p className="text-xs text-green-600 mt-1">
                    {editForm.otherSurroundingArea.length} areas selected
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex space-x-2 pt-2">
              <button
                onClick={handleSave}
                className="bg-green-500 hover:bg-green-600 text-white text-xs px-3 py-1 rounded"
              >
                Save
              </button>
              <button
                onClick={() => onCancel(index)}
                className="bg-gray-500 hover:bg-gray-600 text-white text-xs px-3 py-1 rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            {/* Display Mode */}
            <div className="flex justify-between">
              <span className="text-gray-600">Address:</span>
              <span className="font-medium text-right max-w-[150px]">
                {formatAddress() || 'No address provided'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Property Type:</span>
              <span className="font-medium">{contents.propertyType || 'Not set'}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Residence Type:</span>
              <span className="font-medium">{contents.residenceType || 'Not set'}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Building Age:</span>
              <span className="font-medium">{contents.buildingAge || 'Not set'}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Burglar Bar Type:</span>
              <span className="font-medium">{contents.burglarBarType || 'Not set'}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Situated Type:</span>
              <span className="font-medium">{contents.situatedType || 'Not set'}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Insured Amount:</span>
              <span className="font-medium text-green-600">
                {formatCurrency(contents.insuredValue || 0) || 'Not set'}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Premium:</span>
              <span className="font-medium text-purple-600">
                {formatCurrency(contents.premium || 0) || 'Not set'}
              </span>
            </div>

            {contents.coverType && (
              <div className="flex justify-between">
                <span className="text-gray-600">Cover Type:</span>
                <span className="font-medium">{contents.coverType}</span>
              </div>
            )}

            {/* Security Features */}
            {(contents.hasSurveillanceCamera || contents.hasSecurityGate || contents.inSecurityComplex || contents.hasAlarmRecords) && (
              <div className="pt-2 border-t border-gray-100">
                <span className="text-gray-600 text-xs">Security Features:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {contents.hasSurveillanceCamera && (
                    <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">Camera</span>
                  )}
                  {contents.hasSecurityGate && (
                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Security Gate</span>
                  )}
                  {contents.inSecurityComplex && (
                    <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">Security Complex</span>
                  )}
                  {contents.hasAlarmRecords && (
                    <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">Alarm Records</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddHouseholdContentsModal;
export { EditableHouseholdContentsCard };
export type { HouseholdContents };